// Common
interface REQUEST_DATA_STATE<T> {
  isLoading: boolean
  data?: T
  error?: string
}

interface LiveEventsSport<LiveEventTypeData> {
  [key: string]: REQUEST_DATA_STATE<LiveEventTypeData>
}

interface LiveEventData {
  id: string
  stage: string
  sportsEventId: string
  sdpIsLive: boolean
  sdpScheduledAt: LiveEventScheduledAt
  sdpTypeName: string
  sdpEventName: string
}

interface LiveEventDataCompetitorResult {
  fastest_lap_time: string
  position: number
  status: string
  time: string
}

export interface LiveEventScheduledAt {
  date: string
  format: string
  replacer: string
  template: string
}

export enum LiveEventDataTeamQualifierTypes {
  HOME = 'home',
  AWAY = 'away',
}

// Formula1
export enum LiveEventDataFormula1StageTypes {
  SEASON = 'season',
  EVENT = 'event',
  PRACTICE = 'practice',
  QUALIFYING = 'qualifying',
  RACE = 'race',
  QUALIFYING_PART = 'qualifying_part',
  LAP = 'lap',
  SPORT = 'sport',
}

export enum LiveEventDataFormula1EventStatus {
  NOT_STARTED = 'Not started',
  WARMUP = 'Warmup',
  IN_GRID = 'In grid',
  CLEAR_GRID = 'Grid must be cleared',
}

export interface LiveEventDataFormula1Competitor {
  name: string
  teamname: string
  result?: LiveEventDataCompetitorResult
}

export interface LiveEventDataFormula1SubStage {
  competitors: LiveEventDataFormula1Competitor[]
  sdpIsLive: boolean
  sdpStageHasResults: boolean
}

export interface LiveEventDataFormula1 extends LiveEventData {
  sportsEventType: LiveEventDataSportTypes.FORMULA1
  competitors: LiveEventDataFormula1Competitor[]
  type: string
  status: string
  sdpGPName: string
  laps?: number
  laps_completed?: number
  sdpQualificationCurrentRound?: 0 | 1 | 2 | 3
  sdpQualificationRounds?: number
  sdpQualificationRoundsStatus?: {
    Finished?: number
    Running?: number
    Pending?: number
  }
  sdpStageHasResults: boolean
  subStages?: LiveEventDataFormula1SubStage[]
}

export type LiveEventsSportFormula1 = LiveEventsSport<LiveEventDataFormula1>

// Ski
export enum LiveEventDataSkiStageTypes {
  GESAMT = 'Gesamt',
  ABFAHRT = 'Abfahrt',
  KOMBINATION = 'Kombination',
  SLALOM = 'Slalom',
  SUPERG = 'Super G',
  RIESENSLALOM = 'Riesenslalom',
}

export enum LiveEventDataSkiStageTypesFR {
  GESAMT = 'Total',
  ABFAHRT = 'Départ',
  KOMBINATION = 'Combinaison',
  SLALOM = 'Slalom',
  SUPERG = 'Super G',
  RIESENSLALOM = 'Slalom géant',
}

export interface LiveEventDataSkiCompetitor {
  name: string
  ski: string
  country_code: string
  gap: string
  position: string
  time: string
}
export interface LiveEventDataResultList {
  run?: {
    id: number
    name: string
  }
  competitors?: LiveEventDataSkiCompetitor[]
}
export interface LiveEventDataSki extends LiveEventData {
  sportsEventType: LiveEventDataSportTypes.SKI
  result: {
    resultLists?: LiveEventDataResultList[]
  }
  status: string
  sdpGPName: string
}
export type LiveEventsSportSki = LiveEventsSport<LiveEventDataSki>

// Soccer
export enum LiveEventDataSoccerMatchStatus {
  NOT_STARTED = 'not_started',
  LIVE = 'live',
  FIRST_HALF = '1st_half',
  SECOND_HALF = '2nd_half',
  OVERTIME = 'overtime',
  FIRST_EXTRA = '1st_extra',
  SECOND_EXTRA = '2nd_extra',
  AWAITING_PENALTIES = 'awaiting_penalties',
  PENALTIES = 'penalties',
  PAUSE = 'pause',
  AWAITING_EXTRA_TIME = 'awaiting_extra_time',
  INTERRUPTED = 'interrupted',
  ABANDONED = 'abandoned',
  POSTPONED = 'postponed',
  DELAYED = 'delayed',
  CANCELLED = 'cancelled',
  ENDED = 'ended',
  CLOSED = 'closed',
  HALFTIME = 'halftime',
  FULLTIME = 'full-time',
  EXTRA_TIME = 'extra_time',
  AET = 'aet', //match has ended after extra time
  AP = 'ap', //match has ended after penalties
}

export interface LiveEventDataSoccerTeam {
  name: string
  abbreviation: string
  qualifier: LiveEventDataTeamQualifierTypes
  sdpTeamLogoOptimized: string
}
export interface LiveEventDataSoccerStatus {
  status: string
  aggregate_home_score?: number
  aggregate_away_score?: number
  match_status?: LiveEventDataSoccerMatchStatus
  home_score?: number
  away_score?: number
  period?: number
  clock?: {
    match_time: string
  }
}
export interface LiveEventDataSoccer extends LiveEventData {
  sportsEventType: LiveEventDataSportTypes.SOCCER
  sport_event: {
    competitors: LiveEventDataSoccerTeam[]
  }
  sport_event_status: LiveEventDataSoccerStatus
  sdpIsLive: boolean
  sdpTournamentName: string
  sdpStatus: LiveEventDataStatusTypes
}
export type LiveEventsSportSoccer = LiveEventsSport<LiveEventDataSoccer>

// Hockey
export enum LiveEventDataHockeyMatchStatus {
  NOT_STARTED = 'not_started',
  LIVE = 'live',
  FIRST_PERIOD = '1st_period',
  SECOND_PERIOD = '2nd_period',
  THIRD_PERIOD = '3rd_period',
  OVERTIME = 'overtime',
  FIRST_EXTRA = '1st_extra',
  SECOND_EXTRA = '2nd_extra',
  THIRD_EXTRA = '3rd_extra',
  AWAITING_PENALTIES = 'awaiting_penalties',
  PENALTIES = 'penalties',
  PAUSE = 'pause',
  AWAITING_EXTRA_TIME = 'awaiting_extra_time',
  INTERRUPTED = 'interrupted',
  ABANDONED = 'abandoned',
  POSTPONED = 'postponed',
  DELAYED = 'delayed',
  CANCELLED = 'cancelled',
  ENDED = 'ended',
  AET = 'aet', //match has ended after extra time
  AP = 'ap', //match has ended after penalties
}
export interface LiveEventDataHockeyTeam {
  name: string
  abbreviation: string
  qualifier: LiveEventDataTeamQualifierTypes
  sdpTeamLogoOptimized: string
}
export interface LiveEventDataHockeyStatus {
  status: string
  aggregate_home_score?: number
  aggregate_away_score?: number
  match_status?: LiveEventDataHockeyMatchStatus
  home_score?: number
  away_score?: number
  period?: number
}
export interface LiveEventDataHockey extends LiveEventData {
  sportsEventType: LiveEventDataSportTypes.HOCKEY
  sport_event: {
    scheduled: string
    competitors: LiveEventDataHockeyTeam[]
  }
  sport_event_status: LiveEventDataHockeyStatus
  sdpIsLive: boolean
  sdpTournamentName: string
  sdpStatus: LiveEventDataStatusTypes
}
export type LiveEventsSportHockey = LiveEventsSport<LiveEventDataHockey>

// Tennis
export enum LiveEventDataTennisMatchStatus {
  NOT_STARTED = 'not_started',
  MATCH_ABOUT_TO_START = 'match_about_to_start',
  DELAYED = 'delayed',
  LIVE = 'live',
  FIRST_SET = '1st_set',
  SECOND_SET = '2nd_set',
  THIRD_SET = '3rd_set',
  FOURTH_SET = '4th_set',
  FIFTH_SET = '5th_set',
  ENDED = 'ended',
  WALKOVER = 'walkover', //The match ended in a walkover
  INTERRUPTED = 'interrupted', //The match began, but is stopped for a short time
  SUSPENDED = 'suspended', //The match began, but has been suspended to a later time
  CANCELLED = 'cancelled',
  CANCELED = 'canceled',
  POSTPONED = 'postponed', //The match has been postponed to a future date
  ABANDONED = 'abandoned', //The match began, but was then canceled
  RETIRED = 'retired', //The match is incomplete as the result of one player
  CLOSED = 'closed',
}

export enum LiveEventDataTennisEventStatus {
  NOT_STARTED = 'not_started',
  MATCH_ABOUT_TO_START = 'match_about_to_start',
  DELAYED = 'delayed',
  LIVE = 'live',
  ENDED = 'ended',
  INTERRUPTED = 'interrupted', //The match began, but is stopped for a short time
  SUSPENDED = 'suspended', //The match began, but has been suspended to a later time
  CANCELLED = 'cancelled',
  CANCELED = 'canceled',
  ABANDONED = 'abandoned', //The match began, but was then canceled
  CLOSED = 'closed',
}

export interface LiveEventDataTennisStatus {
  game_state?: LiveEventDataTennisGameState
  period_scores?: LiveEventDataTennisPeriodScores[]
  period?: number
  status: LiveEventDataTennisEventStatus
  match_status: LiveEventDataTennisMatchStatus
}

export interface LiveEventDataTennisGameState {
  sdpAwayScore: string
  sdpHomeScore: string
  tie_break: boolean
}

export interface LiveEventDataTennisPeriodScores {
  away_score: number
  home_score: number
  number: number
}

export interface LiveEventDataTennisCompetitor {
  name: string
  qualifier: LiveEventDataTeamQualifierTypes
}

export interface LiveEventDataTennis extends LiveEventData {
  sportsEventType: LiveEventDataSportTypes.TENNIS
  sport_event: {
    scheduled: string
    competitors: LiveEventDataTennisCompetitor[]
  }
  sport_event_status: LiveEventDataTennisStatus
  sdpIsLive: boolean
  sdpStatus: LiveEventDataStatusTypes
  sdpTournamentName: string
}
export type LiveEventsSportTennis = LiveEventsSport<LiveEventDataTennis>

// Collected
export type LiveEventDatas =
  | LiveEventDataFormula1
  | LiveEventDataSki
  | LiveEventDataTennis
  | LiveEventDataSoccer
  | LiveEventDataHockey

export type LIVE_EVENT_REQUEST_DATA_STATE = REQUEST_DATA_STATE<LiveEventDatas>

export interface LiveEvents {
  [index: string]: LiveEventsSport<LiveEventDatas>
  formula1: LiveEventsSportFormula1
  ski: LiveEventsSportSki
  tennis: LiveEventsSportTennis
  soccer: LiveEventsSportSoccer
  hockey: LiveEventsSportHockey
}

export enum LiveEventDataSportTypes {
  FORMULA1 = 'formula1',
  TENNIS = 'tennis',
  SOCCER = 'soccer',
  HOCKEY = 'hockey',
  SKI = 'ski',
  DEFAULT = 'NO_SPORT',
}

export enum LiveEventDataStatusTypes {
  CANCELLED = 'Cancelled',
  FINISHED = 'Finished',
  RUNNING = 'Running',
  PENDING = 'Pending',
  BETWEEN_RUNS = 'Between_Runs',
  NO_STATUS = '',
}

// Live Center
export interface LiveCenterResponsePayload {
  id: string
  sport: string
  data: LiveEventDatas
  error?: any
}

export interface LiveEventsResponse<T> {
  data: T
  error: string
}
